import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type {
  NotificationTrigger,
  NotificationTriggerUpdatePayload,
} from '../types/notificationTrigger';

/**
 * I-3 trigger 설정 base. 라이브 와이어 정본(direct :8086 프로브, 2026-06-01):
 * - 컨트롤러 실경로 = PLURAL `/admin/v1/notifications/triggers` (singular 는 :8086 에서 404).
 * - channels = JSON string[] (["EMAIL","SMS"]).
 * be-notify 텍스트(singular path + CSV)는 배포된 서비스와 불일치 → 와이어를 따른다.
 * 게이트웨이 FE 경로 = `/api/admin/v1/notifications/triggers`: 게이트웨이 라우트
 * `notification-service-admin-triggers` 가 이 path 만 매칭하고 /api strip → 컨트롤러 plural 로 forward.
 * (현재 gateway UPSTREAM_404 = gateway stale; Lead recreate 후 200.)
 * read 는 배열/CSV 모두 허용해 정규화(이력 방어). FE 모델 = string[].
 */
const BASE = '/api/admin/v1/notifications/triggers';

/** 와이어 trigger: channels 가 CSV 문자열 또는 string[] 둘 다 올 수 있다(보고 불일치 방어). */
interface NotificationTriggerWire {
  triggerCode: string;
  label: string;
  enabled: boolean;
  channels?: string | string[] | null;
}

interface Envelope<T> {
  success: boolean;
  data?: T | null;
  error?: { code?: string; message?: string } | null;
}

/**
 * 와이어 channels → 정규화 string[]. 라이브 정본은 배열(["EMAIL","SMS"])이나, 보고가
 * 엇갈렸던 이력 + 방어를 위해 CSV 문자열도 허용해 동일 string[] 로 정규화한다.
 */
export const parseChannels = (raw: string | string[] | null | undefined): string[] => {
  const list = Array.isArray(raw) ? raw : (raw ?? '').split(',');
  return list.map((c) => c.trim()).filter((c) => c.length > 0);
};

const toModel = (wire: NotificationTriggerWire): NotificationTrigger => ({
  triggerCode: wire.triggerCode,
  label: wire.label,
  enabled: wire.enabled,
  channels: parseChannels(wire.channels),
});

class TriggerApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(status: number, code: string | undefined, message: string) {
    super(message);
    this.name = 'TriggerApiError';
    this.status = status;
    this.code = code;
  }
}

export { TriggerApiError };

const readEnvelope = async <T>(res: Response, fallbackMsg: string): Promise<T> => {
  let env: Envelope<T> | null = null;
  try {
    env = (await res.json()) as Envelope<T>;
  } catch {
    env = null;
  }
  if (!res.ok || !env || env.success !== true) {
    throw new TriggerApiError(res.status, env?.error?.code, env?.error?.message ?? fallbackMsg);
  }
  if (env.data == null) {
    throw new TriggerApiError(res.status, env.error?.code, fallbackMsg);
  }
  return env.data;
};

export const notificationTriggersQueryKey = () =>
  ['admin', 'notification-triggers'] as const;

export const useNotificationTriggersQuery = () =>
  useQuery<NotificationTrigger[]>({
    queryKey: notificationTriggersQueryKey(),
    queryFn: async () => {
      const res = await adminFetch(BASE);
      const wire = await readEnvelope<NotificationTriggerWire[]>(
        res,
        '알림 트리거 목록을 불러오지 못했습니다',
      );
      return wire.map(toModel);
    },
    staleTime: 30_000,
  });

export interface UpdateTriggerInput {
  triggerCode: string;
  body: NotificationTriggerUpdatePayload;
}

export const useUpdateNotificationTriggerMutation = () => {
  const qc = useQueryClient();
  return useMutation<NotificationTrigger, TriggerApiError, UpdateTriggerInput>({
    mutationFn: async ({ triggerCode, body }) => {
      const path = `${BASE}/${encodeURIComponent(triggerCode)}`;
      // 라이브 와이어 정본(direct :8086 프로브): channels = JSON string[] (be-notify 텍스트의
      // CSV/singular 는 배포된 서비스와 불일치 — 실제는 plural path + array). 배열 그대로 송신.
      const wireBody = { enabled: body.enabled, channels: body.channels };
      const idempotencyKey = idempotencyKeyFor('PUT', path, wireBody);
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify(wireBody),
      });
      const wire = await readEnvelope<NotificationTriggerWire>(
        res,
        '알림 트리거 설정에 실패했습니다',
      );
      return toModel(wire);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationTriggersQueryKey() });
    },
  });
};
